import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Search, Plus, Trash2, MapPin, Users, Building2, Camera, UploadCloud, Megaphone, SlidersHorizontal, BadgeCheck, Shield } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (p) => (p?.startsWith("http") ? p : p ? `${BACKEND_URL}/api/files/${p}` : "");

const CATS = {
  employee: ["creator", "artist", "technician"],
  employer: ["producer", "studio", "investor", "brand"],
  equipment: ["camera", "lighting", "audio", "grip", "other"],
  location: ["house", "studio", "outdoor", "venue", "other"],
};

const emptyProfile = (side) => ({ side, name: "", category: CATS[side][0], headline: "", bio: "", location: "", skills: "", rate: "", rate_per_day: "", available: true, website: "", avatar: "" });
const emptyPost = (side) => ({ side, title: "", body: "", category: CATS[side][0], budget: "", location: "" });
const emptyRental = (type) => ({ type, title: "", description: "", category: CATS[type][0], price_per_day: "", location: "", contact: "", image: "" });

function AddProfileDialog({ side, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyProfile(side));
  const fileRef = useRef(null);
  const upAvatar = async (e) => { const file = e.target.files?.[0]; if (!file) return; try { const r = await api.uploadImage(file); setF(s => ({ ...s, avatar: r.path })); } catch { toast.error("Image upload failed"); } };
  const save = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    try { await api.saveNetProfile({ ...f, rate_per_day: Number(f.rate_per_day) || 0, skills: f.skills.split(",").map(s => s.trim()).filter(Boolean) }); setOpen(false); setF(emptyProfile(side)); onSaved(); toast.success("Profile saved"); }
    catch { toast.error("Could not save"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid={`add-profile-${side}`} size="sm" className="kinetic-btn rounded-sm"><Plus className="w-3.5 h-3.5 mr-1" /> Enlist</Button></DialogTrigger>
      <DialogContent className="bg-[#0C0C0C] border-white/10 text-white rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-sans">{side === "employee" ? "Enlist as talent" : "List your company"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="mono text-white/60">Name</Label><Input data-testid="np-name" value={f.name} onChange={e => setF(s => ({ ...s, name: e.target.value }))} className="bg-black border-white/15 rounded-sm" /></div>
          <div><Label className="mono text-white/60">Category</Label>
            <Select value={f.category} onValueChange={v => setF(s => ({ ...s, category: v }))}>
              <SelectTrigger data-testid="np-category" className="bg-black border-white/15 rounded-sm mono"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0C0C0C] border-white/15 text-white">{CATS[side].map(c => <SelectItem key={c} value={c} className="mono">{c.toUpperCase()}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="mono text-white/60">Headline</Label><Input data-testid="np-headline" value={f.headline} onChange={e => setF(s => ({ ...s, headline: e.target.value }))} className="bg-black border-white/15 rounded-sm" /></div>
          <div><Label className="mono text-white/60">Bio</Label><Textarea data-testid="np-bio" value={f.bio} onChange={e => setF(s => ({ ...s, bio: e.target.value }))} className="bg-black border-white/15 rounded-sm" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="mono text-white/60">Location</Label><Input data-testid="np-location" value={f.location} onChange={e => setF(s => ({ ...s, location: e.target.value }))} className="bg-black border-white/15 rounded-sm" /></div>
            <div><Label className="mono text-white/60">{side === "employee" ? "Rate" : "Website"}</Label><Input data-testid="np-rate" value={side === "employee" ? f.rate : f.website} onChange={e => setF(s => ({ ...s, [side === "employee" ? "rate" : "website"]: e.target.value }))} className="bg-black border-white/15 rounded-sm" /></div>
          </div>
          {side === "employee" && <div><Label className="mono text-white/60">Skills (comma separated)</Label><Input data-testid="np-skills" value={f.skills} onChange={e => setF(s => ({ ...s, skills: e.target.value }))} className="bg-black border-white/15 rounded-sm" /></div>}
          {side === "employee" && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mono text-white/60">Rate / day (₹)</Label><Input data-testid="np-rate-per-day" type="number" value={f.rate_per_day} onChange={e => setF(s => ({ ...s, rate_per_day: e.target.value }))} className="bg-black border-white/15 rounded-sm" /></div>
              <div className="flex items-end gap-2 pb-1"><Switch data-testid="np-available" checked={f.available} onCheckedChange={v => setF(s => ({ ...s, available: v }))} /><span className="mono text-white/60">Available for work</span></div>
            </div>
          )}
          <div className="flex items-center gap-3">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upAvatar} />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-sm border-white/20 bg-transparent text-white hover:bg-white/10"><UploadCloud className="w-4 h-4 mr-2" />Photo/Logo</Button>
            {f.avatar && <img src={fileUrl(f.avatar)} alt="" className="w-10 h-10 object-cover rounded-sm" />}
          </div>
        </div>
        <DialogFooter><Button data-testid="np-submit" onClick={save} className="kinetic-btn rounded-sm">Save profile</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddPostDialog({ side, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyPost(side));
  const save = async () => {
    if (!f.title.trim()) return toast.error("Title required");
    try { await api.createNetPost(f); setOpen(false); setF(emptyPost(side)); onSaved(); toast.success("Posted"); } catch { toast.error("Could not post"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid={`add-post-${side}`} size="sm" variant="outline" className="rounded-sm border-white/20 bg-transparent text-white/80 hover:bg-white/10"><Megaphone className="w-3.5 h-3.5 mr-1" /> {side === "employee" ? "Looking for" : "Post lookout"}</Button></DialogTrigger>
      <DialogContent className="bg-[#0C0C0C] border-white/10 text-white rounded-sm max-w-lg">
        <DialogHeader><DialogTitle className="font-sans">{side === "employee" ? "What are you looking for?" : "Post a requirement"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="mono text-white/60">Title</Label><Input data-testid="post-title" value={f.title} onChange={e => setF(s => ({ ...s, title: e.target.value }))} className="bg-black border-white/15 rounded-sm" /></div>
          <div><Label className="mono text-white/60">Details</Label><Textarea data-testid="post-body" value={f.body} onChange={e => setF(s => ({ ...s, body: e.target.value }))} className="bg-black border-white/15 rounded-sm" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="mono text-white/60">Category</Label>
              <Select value={f.category} onValueChange={v => setF(s => ({ ...s, category: v }))}>
                <SelectTrigger data-testid="post-category" className="bg-black border-white/15 rounded-sm mono"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0C0C0C] border-white/15 text-white">{CATS[side].map(c => <SelectItem key={c} value={c} className="mono">{c.toUpperCase()}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mono text-white/60">Budget</Label><Input data-testid="post-budget" value={f.budget} onChange={e => setF(s => ({ ...s, budget: e.target.value }))} className="bg-black border-white/15 rounded-sm" /></div>
            <div><Label className="mono text-white/60">Location</Label><Input data-testid="post-location" value={f.location} onChange={e => setF(s => ({ ...s, location: e.target.value }))} className="bg-black border-white/15 rounded-sm" /></div>
          </div>
        </div>
        <DialogFooter><Button data-testid="post-submit" onClick={save} className="kinetic-btn rounded-sm">Publish</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarketColumn({ side, title, icon: Icon }) {
  const [mode, setMode] = useState("directory");
  const [q, setQ] = useState(() => (side === "employee" ? (new URLSearchParams(window.location.search).get("q") || "") : ""));
  const [category, setCategory] = useState("");
  const [skill, setSkill] = useState("");
  const [availableOnly, setAvailableOnly] = useState(false);
  const [maxRate, setMaxRate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [posts, setPosts] = useState([]);
  const load = useCallback(async () => {
    if (mode === "directory") {
      const params = { side, q };
      if (category) params.category = category;
      if (side === "employee") {
        if (skill) params.skill = skill;
        if (availableOnly) params.available = "true";
        if (maxRate) params.max_rate = Number(maxRate);
      }
      setProfiles(await api.netProfiles(params));
    } else setPosts(await api.netPosts(side, q));
  }, [mode, q, side, category, skill, availableOnly, maxRate]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const delProfile = async (id) => { await api.deleteNetProfile(id); load(); };
  const delPost = async (id) => { await api.deleteNetPost(id); load(); };

  return (
    <div className="border border-white/10 bg-[#0C0C0C] rounded-lg flex flex-col" data-testid={`market-${side}`}>
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center justify-between gap-2">
          <div className="mono text-kinetic flex items-center gap-2"><Icon className="w-4 h-4" /> {title}</div>
          <div className="flex gap-2">
            {mode === "directory" ? <AddProfileDialog side={side} onSaved={load} /> : <AddPostDialog side={side} onSaved={load} />}
          </div>
        </div>
        <div className="flex gap-1 mt-3">
          {["directory", "posts"].map(m => (
            <button key={m} data-testid={`${side}-tab-${m}`} onClick={() => setMode(m)}
              className={`mono px-3 h-8 rounded-sm border ${mode === m ? "border-kinetic text-kinetic bg-kinetic/10" : "border-white/15 text-white/55 hover:text-white"}`}>
              {m === "directory" ? "DIRECTORY" : (side === "employee" ? "LOOKING FOR" : "LOOKOUT")}
            </button>
          ))}
        </div>
        <div className="relative mt-3">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input data-testid={`search-${side}`} value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className="bg-black border-white/15 rounded-sm pl-9 h-9" />
        </div>
        {mode === "directory" && (
          <div className="mt-2">
            <button data-testid={`filters-toggle-${side}`} onClick={() => setShowFilters(v => !v)} className="mono text-white/50 hover:text-kinetic inline-flex items-center gap-1"><SlidersHorizontal className="w-3 h-3" /> FILTERS</button>
            {showFilters && (
              <div className="mt-2 grid grid-cols-2 gap-2" data-testid={`filters-${side}`}>
                <Select value={category || "all"} onValueChange={v => setCategory(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid={`filter-category-${side}`} className="bg-black border-white/15 rounded-sm mono h-8"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent className="bg-[#0C0C0C] border-white/15 text-white">
                    <SelectItem value="all" className="mono">ALL {side === "employee" ? "ROLES" : "TYPES"}</SelectItem>
                    {CATS[side].map(c => <SelectItem key={c} value={c} className="mono">{c.toUpperCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
                {side === "employee" ? (
                  <>
                    <Input data-testid="filter-skill" value={skill} onChange={e => setSkill(e.target.value)} placeholder="Skill" className="bg-black border-white/15 rounded-sm h-8" />
                    <Input data-testid="filter-max-rate" type="number" value={maxRate} onChange={e => setMaxRate(e.target.value)} placeholder="Max ₹/day" className="bg-black border-white/15 rounded-sm h-8" />
                    <label className="flex items-center gap-2 mono text-white/60"><Switch data-testid="filter-available" checked={availableOnly} onCheckedChange={setAvailableOnly} /> Available only</label>
                  </>
                ) : (
                  <div className="mono text-white/40 col-span-1 flex items-center">Producers · Studios · Investors · Brands</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="p-4 space-y-3 min-h-[200px]">
        {mode === "directory" ? (
          profiles.length === 0 ? <div className="mono text-white/30 text-center py-8">NO PROFILES YET</div> :
            profiles.map(p => (
              <div key={p.id} className="border border-white/10 rounded-sm p-3 flex gap-3" data-testid={`profile-${p.id}`}>
                <Avatar className="w-11 h-11 rounded-sm"><AvatarImage src={fileUrl(p.avatar)} /><AvatarFallback className="bg-white/10 rounded-sm">{p.name?.[0]}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="font-sans font-bold truncate">{p.name}</div>
                    <span className="mono text-kinetic">{p.category?.toUpperCase()}</span>
                  </div>
                  {p.headline && <div className="text-white/70 text-sm truncate">{p.headline}</div>}
                  <div className="mono text-white/40 mt-1 flex items-center gap-2 flex-wrap">
                    {p.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{p.location}</span>}
                    {p.rate && <span>{p.rate}</span>}
                  </div>
                  {p.skills?.length > 0 && <div className="flex gap-1 flex-wrap mt-1">{p.skills.map((s, i) => <span key={i} className="mono text-white/50 border border-white/10 px-1.5 py-0.5 rounded-sm">{s}</span>)}</div>}
                </div>
                <Trash2 data-testid={`profile-del-${p.id}`} onClick={() => delProfile(p.id)} className="w-4 h-4 text-white/25 hover:text-coral shrink-0" />
              </div>
            ))
        ) : (
          posts.length === 0 ? <div className="mono text-white/30 text-center py-8">NO POSTS YET</div> :
            posts.map(p => (
              <div key={p.id} className="border border-white/10 rounded-sm p-3" data-testid={`post-${p.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-sans font-bold">{p.title}</div>
                  <Trash2 data-testid={`post-del-${p.id}`} onClick={() => delPost(p.id)} className="w-4 h-4 text-white/25 hover:text-coral" />
                </div>
                {p.body && <p className="text-white/70 text-sm mt-1">{p.body}</p>}
                <div className="mono text-white/40 mt-2 flex items-center gap-3 flex-wrap">
                  <span className="text-kinetic">{p.category?.toUpperCase()}</span>
                  {p.budget && <span>💰 {p.budget}</span>}
                  {p.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{p.location}</span>}
                  <span>· {p.author_name}</span>
                </div>
              </div>
            ))
        )}
      </div>
    </div>
  );
}

function AddRentalDialog({ type, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyRental(type));
  const fileRef = useRef(null);
  useEffect(() => { setF(emptyRental(type)); }, [type]);
  const upImg = async (e) => { const file = e.target.files?.[0]; if (!file) return; try { const r = await api.uploadImage(file); setF(s => ({ ...s, image: r.path })); } catch { toast.error("Image upload failed"); } };
  const save = async () => {
    if (!f.title.trim()) return toast.error("Title required");
    try { await api.createRental({ ...f, price_per_day: Number(f.price_per_day) || 0 }); setOpen(false); setF(emptyRental(type)); onSaved(); toast.success("Listed"); } catch { toast.error("Could not list"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid={`add-rental-${type}`} size="sm" className="kinetic-btn rounded-sm"><Plus className="w-3.5 h-3.5 mr-1" /> List {type}</Button></DialogTrigger>
      <DialogContent className="bg-[#0C0C0C] border-white/10 text-white rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-sans">List {type === "equipment" ? "equipment" : "a location"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="mono text-white/60">Title</Label><Input data-testid="rental-title" value={f.title} onChange={e => setF(s => ({ ...s, title: e.target.value }))} className="bg-black border-white/15 rounded-sm" /></div>
          <div><Label className="mono text-white/60">Description</Label><Textarea data-testid="rental-desc" value={f.description} onChange={e => setF(s => ({ ...s, description: e.target.value }))} className="bg-black border-white/15 rounded-sm" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="mono text-white/60">Category</Label>
              <Select value={f.category} onValueChange={v => setF(s => ({ ...s, category: v }))}>
                <SelectTrigger data-testid="rental-category" className="bg-black border-white/15 rounded-sm mono"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0C0C0C] border-white/15 text-white">{CATS[type].map(c => <SelectItem key={c} value={c} className="mono">{c.toUpperCase()}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mono text-white/60">₹/day</Label><Input data-testid="rental-price" type="number" value={f.price_per_day} onChange={e => setF(s => ({ ...s, price_per_day: e.target.value }))} className="bg-black border-white/15 rounded-sm" /></div>
            <div><Label className="mono text-white/60">Location</Label><Input data-testid="rental-location" value={f.location} onChange={e => setF(s => ({ ...s, location: e.target.value }))} className="bg-black border-white/15 rounded-sm" /></div>
          </div>
          <div><Label className="mono text-white/60">Contact</Label><Input data-testid="rental-contact" value={f.contact} onChange={e => setF(s => ({ ...s, contact: e.target.value }))} className="bg-black border-white/15 rounded-sm" /></div>
          <div className="flex items-center gap-3">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upImg} />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-sm border-white/20 bg-transparent text-white hover:bg-white/10"><UploadCloud className="w-4 h-4 mr-2" />Photo</Button>
            {f.image && <img src={fileUrl(f.image)} alt="" className="w-14 h-10 object-cover rounded-sm" />}
          </div>
        </div>
        <DialogFooter><Button data-testid="rental-submit" onClick={save} className="kinetic-btn rounded-sm">List it</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Rentals() {
  const [type, setType] = useState("equipment");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const load = useCallback(async () => setItems(await api.rentals(type, q)), [type, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const del = async (id) => { await api.deleteRental(id); load(); };
  return (
    <div className="space-y-5" data-testid="rentals">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1">
          {[["equipment", "EQUIPMENT", Camera], ["location", "LOCATIONS", MapPin]].map(([k, l, Icon]) => (
            <button key={k} data-testid={`rental-tab-${k}`} onClick={() => setType(k)}
              className={`mono px-4 h-9 rounded-sm border inline-flex items-center gap-2 ${type === k ? "border-kinetic text-kinetic bg-kinetic/10" : "border-white/15 text-white/55 hover:text-white"}`}>
              <Icon className="w-3.5 h-3.5" /> {l}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative w-56"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input data-testid="rental-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className="bg-black border-white/15 rounded-sm pl-9 h-9" /></div>
          <AddRentalDialog type={type} onSaved={load} />
        </div>
      </div>
      {items.length === 0 ? <div className="border border-dashed border-white/15 rounded-lg p-16 text-center mono text-white/40">NO {type.toUpperCase()} LISTINGS YET</div> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(r => (
            <div key={r.id} className="border border-white/10 bg-[#0C0C0C] rounded-lg overflow-hidden" data-testid={`rental-${r.id}`}>
              <div className="aspect-video bg-black overflow-hidden">
                {r.image ? <img src={fileUrl(r.image)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/20"><Camera className="w-8 h-8" /></div>}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="font-sans font-bold">{r.title}</div>
                  <Trash2 data-testid={`rental-del-${r.id}`} onClick={() => del(r.id)} className="w-4 h-4 text-white/25 hover:text-coral" />
                </div>
                {r.description && <p className="text-white/60 text-sm mt-1 line-clamp-2">{r.description}</p>}
                <div className="flex items-center justify-between mt-2 mono">
                  <span className="text-kinetic">₹{Number(r.price_per_day).toLocaleString("en-IN")}/DAY</span>
                  {r.location && <span className="text-white/40 inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{r.location}</span>}
                </div>
                <div className="mono text-white/40 mt-1">{r.category?.toUpperCase()} · {r.owner_name}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NetworkSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.netSettings().then(setS); }, []);
  const save = async (patch) => { const next = { ...s, ...patch }; setS(next); try { await api.updateNetSettings(patch); toast.success("Settings saved"); } catch { /* ignore */ } };
  if (!s) return <div className="mono text-white/40">LOADING…</div>;
  return (
    <div className="max-w-xl space-y-4" data-testid="network-settings">
      <div className="border border-white/10 bg-[#0C0C0C] rounded-lg p-6 space-y-4">
        <div className="mono text-kinetic flex items-center gap-2"><BadgeCheck className="w-4 h-4" /> VERIFICATION</div>
        <div className="flex items-center justify-between">
          <div><div className="font-sans font-bold">Request a verified account</div><div className="mono text-white/40">Get a blue check to build trust with stakeholders</div></div>
          <Switch data-testid="net-verified-request" checked={!!s.verified_request} onCheckedChange={v => save({ verified_request: v })} />
        </div>
        {s.verified_request && <div className="mono text-kinetic">✓ VERIFICATION REQUESTED — under review</div>}
      </div>
      <div className="border border-white/10 bg-[#0C0C0C] rounded-lg p-6 space-y-4">
        <div className="mono text-kinetic flex items-center gap-2"><Shield className="w-4 h-4" /> PRIVACY & REVIEWS</div>
        <div className="flex items-center justify-between"><div className="font-sans font-bold">Allow peer reviews</div><Switch data-testid="net-peer-reviews" checked={!!s.peer_reviews_enabled} onCheckedChange={v => save({ peer_reviews_enabled: v })} /></div>
        <div>
          <Label className="mono text-white/60">Profile privacy</Label>
          <Select value={s.privacy || "public"} onValueChange={v => save({ privacy: v })}>
            <SelectTrigger data-testid="net-privacy" className="bg-black border-white/15 rounded-sm mono"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0C0C0C] border-white/15 text-white">{["public", "connections", "private"].map(p => <SelectItem key={p} value={p} className="mono">{p.toUpperCase()}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between"><div className="font-sans font-bold">Parental / safe mode</div><Switch data-testid="net-parental" checked={!!s.parental} onCheckedChange={v => save({ parental: v })} /></div>
      </div>
    </div>
  );
}

export default function NetworkCircuit() {
  const [view, setView] = useState("marketplace");
  return (
    <div className="space-y-6" data-testid="network-circuit">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="mono text-white/40">NETWORKING CIRCUIT · MAKERS ↔ STUDIOS</div>
          <h1 className="text-4xl font-extrabold tracking-tight mt-2">Networking Circuit</h1>
          <p className="text-white/60 mt-2">Connect talent with studios & investors. Rent gear and locations.</p>
        </div>
        <div className="flex gap-1">
          {[["marketplace", "MARKETPLACE"], ["rentals", "RENTALS"], ["settings", "SETTINGS"]].map(([k, l]) => (
            <button key={k} data-testid={`view-${k}`} onClick={() => setView(k)}
              className={`mono px-4 h-9 rounded-sm border ${view === k ? "border-kinetic text-kinetic bg-kinetic/10" : "border-white/15 text-white/55 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>

      {view === "marketplace" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <MarketColumn side="employee" title="TALENT MARKETPLACE" icon={Users} />
          <MarketColumn side="employer" title="EMPLOYER MARKETPLACE" icon={Building2} />
        </div>
      )}
      {view === "rentals" && <Rentals />}
      {view === "settings" && <NetworkSettings />}
    </div>
  );
}
